import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { IntlProvider, FormattedMessage, useIntl } from 'react-intl';
import s from './MapView.module.scss';
import bs from '../../styles/bootstrap-overrides.scss';
import DeviceTile from '../DeviceTile/DeviceTile';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import moment from 'moment-timezone';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import droplet from '../../assets/droplet-icon.svg';
import pendingAlarm from '../../assets/alarm-outline-small.svg';
import dropletClusterMarker from '../../assets/droplet-with-number.png';
import warning from '../../assets/warning-icon.svg';
import error from '../../assets/error-icon.svg';
import fullScreen from '../../assets/fullscreen-icon.svg';
import closeButton from '../../assets/close-button-icon.svg';
import SearchBar from '../SearchBar/SearchBar';
import Dropdown from '../WSAControls/DroprdownContainer/Dropdown';
import Map from '../Map/Map';
import Markers from '../Map/Markers';
import MapInfoWindow from '../Map/MapInfoWindow';
import Button from 'react-bootstrap/Button';
import { ALARM_STATUS_NEW, ALARM_STATUS_PENDING } from '../../constants';
import { Link } from 'react-router-dom';
import { push } from 'redux-first-history';

const MapView = ({
  devices = {},
  handleClick,
  sensorData = [],
  eventData = [],
  alarmsData = [],
  flowSequences = [],
  allowedUnits = { userProfileUnits: [] }
}) => {
  const intl = useIntl();
  const googleMapRef = useRef(null);
  
  // State variables
  const [mapLoaded, setMapLoaded] = useState(false);
  const [markerClustererLoaded, setMarkerClustererLoaded] = useState(false);
  const [markerDrawn, setMarkerDrawn] = useState(false);
  const [showMapOverlay, setShowMapOverlay] = useState(false);
  const [unitSerialNumber, setUnitSerialNumber] = useState(null);
  const [location, setLocation] = useState('');
  const [isTaggedOut, setIsTaggedOut] = useState(0);
  const [tagOutByUser, setTagOutByUser] = useState('');
  const [schedulesCount, setSchedulesCount] = useState(0);
  const [deviceSerialNumber, setDeviceSerialNumber] = useState('');
  const [unitName, setUnitName] = useState('');
  const [country, setCountry] = useState('');
  const [position, setPosition] = useState({});
  const [searchQuery, setSearchQuery] = useState({
    keywords: '',
    type: 0
  });
  const [isFullScreen, setIsFullScreen] = useState(false);
  
  // Refs for values that don't trigger re-renders
  const mapOverlayRef = useRef({});
  const markerClusterRef = useRef(null);
  
  const dropDownData = [
    {
      label: 'All',
      value: '0',
      isDisabled: false
    },
    {
      label: 'Warnings',
      value: '1',
      isDisabled: false
    },
    {
      label: 'New Alarms',
      value: '2',
      isDisabled: false
    },
    {
      label: 'Pending Alarms',
      value: '3',
      isDisabled: false
    },
    {
      label: 'Offline',
      value: '4',
      isDisabled: false
    }
  ];

  const defaultLocation = { lat: -37.840935, lng: 144.946457 };

  const onMapClick = (map) => {
    removeMapOverlay(map);
  };

  const onMapLoad = (map) => {
    setMapLoaded(true);
  };

  const onMarkerClusterLoad = (MarkerClusterer) => {
    // Store the MarkerClusterer constructor, not an instance
    markerClusterRef.current = MarkerClusterer;
    setMarkerClustererLoaded(true);
  };

  const handleTileClick = (unitSerialNumber) => {
    if (handleClick) {
      handleClick(unitSerialNumber);
    }
  };

  const onMarkerClick = (marker) => {
    if (marker && marker.device && marker.device.UnitId) {
      setShowMapOverlay(true);
      setLocation(marker.device.InstallationCity);
      setCountry(marker.device.InstallationCountry);
      setIsTaggedOut(marker.device.IsTaggedOut);
      setPosition(marker.position);
      setTagOutByUser(marker.device.TagOutByUser);
      setUnitName(marker.device.Name);
      setUnitSerialNumber(marker.device.UnitSerialNumber);
      setDeviceSerialNumber(marker.device.DeviceSerialNumber);
      setSchedulesCount(marker.device.SchedulesCount);
    }
  };

  const onMarkerReDrawn = (newMarkerObj) => {
    //Code to execute on marker redrawn
    if (!markerDrawn) {
      setMarkerDrawn(true);
    }
  };

  const onMarkerDrawn = (markerObj) => {
    if (!markerDrawn) {
      setMarkerDrawn(true);
    }
    removeMapOverlay();
  };

  const onMarkerRemoved = (markerObj) => {
    removeMapOverlay();
  };

  const removeMapOverlay = (map) => {
    setShowMapOverlay(false);
    setLocation('');
    setCountry('');
    setIsTaggedOut(0);
    setTagOutByUser('');
    setSchedulesCount(0);
    setPosition({});
    setUnitName('');
    setUnitSerialNumber(null);
    setDeviceSerialNumber('');

    if (mapOverlayRef.current && mapOverlayRef.current.overlay && mapOverlayRef.current.overlay.setMap) {
      mapOverlayRef.current.overlay.setMap(null);
    }
  };

  const getFormattedUnits = () => {
    let devicesObj = Object.values(devices);
    let formattedUnits = devicesObj.map(device => {
      //Unit Address
      let address = `${device.InstallationStreetAddress || ''}, ${device.InstallationCity || ''}, ${device.InstallationRegion ||
        ''},${device.InstallationPostCode || ''} ${device.InstallationCountry || ''} `;

      device.address = address;

      //Unit Current Status
      let deviceSensorData = sensorData.filter(sensor => sensor.unitserialnumber === device.UnitSerialNumber);
      const timestamp = deviceSensorData && deviceSensorData.map(item => item.timestamp);
      const deviceOnline = timestamp && timestamp[0] && moment(timestamp[0]).isBetween(moment().subtract(1, 'minute'), moment());
      device.Online = deviceOnline;

      //position
      if (device.CommissionGpsLatitude && device.CommissionGpsLongitude) {
        device.position = { lat: device.CommissionGpsLatitude, lng: device.CommissionGpsLongitude };
      }

      //Unit EventType and Icon
      let filteredEvents =
        eventData.length > 0 && eventData.filter(event => event.unitSerialNumber === device.UnitSerialNumber);
      const warnings =
        filteredEvents && filteredEvents.length > 0 && filteredEvents[0].events && filteredEvents[0].events.filter(item => item.type === 1);

      const newAlarms =
        alarmsData &&
        alarmsData.filter(event => {
          return event.EventStatus === ALARM_STATUS_NEW && event.UnitNumber === device.UnitNumber;
        });

      const pendingAlarms =
        alarmsData &&
        alarmsData.filter(event => {
          return event.EventStatus === ALARM_STATUS_PENDING && event.UnitNumber === device.UnitNumber;
        });

      let warningCount = (warnings && warnings.length) || 0;
      let newAlarmsCount = (newAlarms && newAlarms.length) || 0;
      let pendingAlarmsCount = (pendingAlarms && pendingAlarms.length) || 0;

      let icon = newAlarmsCount > 0 ? error : pendingAlarmsCount > 0 ? pendingAlarm : warningCount > 0 ? warning : droplet;
      device.icon = icon;
      device.newAlarm = newAlarmsCount > 0 ? true : false;
      device.pendingAlarm = pendingAlarmsCount > 0 ? true : false;
      device.warning = warningCount > 0 ? true : false;

      return device;
    });
    return formattedUnits;
  };

  const onSearchKeywordChange = (keyword) => {
    removeMapOverlay();

    setSearchQuery({
      ...searchQuery,
      keywords: keyword
    });
  };

  const onSearchTypeChange = (type) => {
    removeMapOverlay();

    setSearchQuery({
      ...searchQuery,
      type: type.value
    });
  };

  const performSearch = (units) => {
    if (searchQuery.keywords) {
      units = units.filter(unit => unit.address.toLowerCase().indexOf(searchQuery.keywords.toLowerCase()) >= 0);
    }
    let type = searchQuery.type && searchQuery.type * 1;
    if (type && type <= 3) {
      switch (type) {
        case 1:
          units = units.filter(unit => unit.warning);
          break;
        case 2:
          units = units.filter(unit => unit.newAlarm);
          break;
        case 3:
          units = units.filter(unit => unit.pendingAlarm);
          break;
        default:
          break;
      }
    }

    if (type === 4) {
      units = units.filter(unit => !unit.Online);
    }

    return units;
  };

  const setOverlay = (overlay) => {
    mapOverlayRef.current = overlay;
  };

  const onFullscreenButtonClick = (id) => {
    setIsFullScreen(true);

    let element = document.getElementById('mapViewContainer');
    if (element.requestFullscreen) {
      element.requestFullscreen();
    }
    if (element.webkitRequestFullScreen) {
      element.webkitRequestFullScreen();
    }
    if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen();
    }
  };

  const onFullScreenCloseButtonClick = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  };

  const onFullScreenExit = () => {
    setIsFullScreen(false);
  };

  let formattedUnits = getFormattedUnits();
  formattedUnits = performSearch(formattedUnits);

  // Get the current locale and messages from the intl context
  const locale = intl.locale || 'en';
  const messages = {};  // You would normally get this from your translations

  // Create an IntlProvider to ensure all child components have access to intl
  return (
    <IntlProvider locale={locale} messages={messages}>
      <div className={s.mapContainer} id="mapViewContainer">
        {!markerDrawn && <LoadingSpinner centeredLoading={false} />}
        <Row className={isFullScreen ? `d-md-flex ${s.fullScreenSearch}` : 'd-none d-md-flex'}>
          <div className={s.searchBarContainer}>
            <SearchBar
              searchHandler={onSearchKeywordChange}
              clearSearchInVisible={true}
              placeHolderTextId="unitlistmap.enterUnitAddress"
            />
          </div>
          <Col xs={12} md={4} lg={3} xl={2} className={s.mapDropdown}>
            {isFullScreen ? (
              <Row>
                <Col xs={6} className={'d-md-none'}>
                  <img
                    src={closeButton}
                    onClick={onFullScreenCloseButtonClick}
                    alt="close"
                    style={{ height: '30px', marginLeft: '10px' }}
                  />
                </Col>
                <Col xs={6} className={`d-md-none ${s.mapSearchDropdown}`}>
                  <Dropdown
                    dataArray={dropDownData}
                    controlData={{ placeholderText: 'All', customClassName: s.dropdownClass }}
                    onDropdownChange={onSearchTypeChange}
                  />
                </Col>
              </Row>
            ) : (
              <Dropdown
                dataArray={dropDownData}
                controlData={{ placeholderText: 'All', customClassName: '' }}
                onDropdownChange={onSearchTypeChange}
              />
            )}
          </Col>
        </Row>

        <Map
          apiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}
          mapContainerCssStyle={s.mapContainerStyle}
          onMapClick={onMapClick}
          onMapLoad={onMapLoad}
          onMarkerClusterLoad={onMarkerClusterLoad}
          fullScreenIcon={fullScreen}
          onFullScreenButtonClick={onFullscreenButtonClick}
          onFullScreenExit={onFullScreenExit}
          mapContainerStyleFullScreen={s.mapContainerStyleFullScreen}
        >
          <MapInfoWindow
            handleClick={unitSerialNumber => {
              handleTileClick(unitSerialNumber);
            }}
            position={position}
            onOverlayDrawn={setOverlay}
            hideOverlay={!showMapOverlay}
          >
            <ul
              className={s.deviceWrapper}
              unitserialnumber={unitSerialNumber || ''}
              location={location}
              country={country}
            >
              <DeviceTile
                location={location}
                country={country}
                isTaggedOut={isTaggedOut}
                isSchedulesCount={schedulesCount > 0 ? true : false}
                unitName={unitName}
                unitSerialNumber={unitSerialNumber || ''}
                deviceSerialNumber={deviceSerialNumber}
                handleClick={handleTileClick}
                deviceSensorData={sensorData.filter(sensor => sensor.unitserialnumber === unitSerialNumber)}
                deviceEventData={eventData.filter(event => event.unitSerialNumber === unitSerialNumber)}
                deviceAlarmsData={alarmsData.filter(x => x.UnitSerialNumber === unitSerialNumber)}
                flowSequences={flowSequences}
                tagOutMessage={tagOutByUser}
                scheduleMessage="aaa"
              />
            </ul>
          </MapInfoWindow>

          {mapLoaded && markerClustererLoaded && (
            <Markers
              onClick={onMarkerClick}
              onMarkerDrawn={onMarkerDrawn}
              onMarkerReDrawn={onMarkerReDrawn}
              onMarkerRemoved={onMarkerRemoved}
              devices={formattedUnits}
              markerCluster={markerClusterRef.current}
              markerClusterIcon={dropletClusterMarker}
              defaultLocation={defaultLocation}
            />
          )}
        </Map>
        {!isFullScreen && (
          <div className={`container-fluid ${s.seeAllUnits}`}>
            <Row className={s.alignCenter}>
              <Col md={5}>
                <hr />
              </Col>
              <Col md={2} xs={12}>
                <Link to={'/units'}>
                  <Button>
                    <FormattedMessage id="mapview.seeallunits" defaultMessage="SEE ALL UNITS" />
                  </Button>
                </Link>
              </Col>
              <Col md={5}>
                <hr />
              </Col>
            </Row>
          </div>
        )}
      </div>
    </IntlProvider>
  );
};

MapView.propTypes = {
  devices: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  handleClick: PropTypes.func.isRequired,
  sensorData: PropTypes.array,
  eventData: PropTypes.array,
  alarmsData: PropTypes.array,
  flowSequences: PropTypes.array,
  allowedUnits: PropTypes.object
};

export default MapView;